import React, { FC, useEffect, useState } from 'react';
import Table, { TFilter } from '@components/table';
import styles from './styles.module.scss';
import Status from '@components/common/status';
import PriceStatus from '@components/common/price_status';
import Typography from '@components/common/typography';
import RangeDatePicker from '@components/range_date_picker';
import Button from '@components/common/button';
import Drawer from '@components/drawer';
import Input from '@components/common/input';
import Accordion from '@components/common/accordin';
import MultiSelect from '@components/common/multi_select';
import Range from '@components/range';
import { useDebounceValue } from 'usehooks-ts';
import Dropdown from '@components/common/dropdown';
import { useGiftCards } from '@hooks/api/useGiftCards';
import moment from 'moment';
import { EGiftCardStatus, TAdminGiftCard, TGetAdminGiftCardsFilter } from '@xeppt/xeppt-sdk/types';
import { getGiftCardsStatusColor, getGiftCardStatusString } from '@utils/index';
import Checkbox from '@components/common/checkbox';
import { useIsFirstRender } from '@mantine/hooks';
import Textarea from '@components/common/textarea';

const giftCardStatuses = [
    {
        label: 'Issued',
        value: EGiftCardStatus.ISSUED
    },
    {
        label: 'Pending',
        value: EGiftCardStatus.PENDING
    },
    {
        label: 'Active',
        value: EGiftCardStatus.ACTIVE
    },
    {
        label: 'Redeemed',
        value: EGiftCardStatus.REDEEMED
    },
    {
        label: 'Partially redeemed',
        value: EGiftCardStatus.PARTIALLY_REDEEMED
    },
    {
        label: 'Cancel requested',
        value: EGiftCardStatus.CANCEL_REQUESTED
    },
    {
        label: 'Cancelled',
        value: EGiftCardStatus.CANCELLED
    }
];

interface IProps {
    customer_id?: string;
}

const GiftCardsLayout: FC<IProps> = ({ customer_id }) => {
    const isFirstRender = useIsFirstRender();
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [isGiftCardDrawerOpen, setIsGiftCardDrawerOpen] = useState(false);
    const [rangesValue, setRangesValue] = useState<Partial<TGetAdminGiftCardsFilter>>({
        amount_from: undefined,
        amount_to: undefined,
        available_amount_from: undefined,
        available_amount_to: undefined
    });
    const [debouncedRangesValue, setDebouncedRangesValue] = useDebounceValue(rangesValue, 500);

    const {
        filter,
        onChangeFilter,
        giftCards,
        refetchGiftCards,
        giftCard,
        getSingleGiftCard,
        handleRemoveGiftCardData,
        giftCardsData,
        handleUpdateGiftCardStatus
    } = useGiftCards({ isInitialGiftCards: false, customer_id });

    const [search, setSearch] = useState('');
    const [requestSearch, setRequestSearch] = useDebounceValue('', 500);

    const columns = [
        {
            label: 'Serial number',
            key: 'gift_card_serial_number',
            render: (value: string) => `#${value}`
        },
        {
            label: 'Amount',
            key: 'amount',
            isSort: true,
            render: (value: number, data: TAdminGiftCard) => `$${value.toFixed(2)} ${data.currency}`
        },
        {
            label: 'Available amount',
            key: 'available_amount',
            isSort: true,
            render: (value: number, data: TAdminGiftCard) => `$${value.toFixed(2)} ${data.currency}`
        },
        {
            label: 'Status',
            key: 'status',
            render: (value: EGiftCardStatus, data: TAdminGiftCard) => (
                <Status
                    variant={getGiftCardsStatusColor(value)}
                    updateOptions={[
                        {
                            label: 'Pending',
                            value: EGiftCardStatus.PENDING
                        },
                        {
                            label: 'Active',
                            value: EGiftCardStatus.ACTIVE
                        },
                        {
                            label: 'Partially redeemed',
                            value: EGiftCardStatus.PARTIALLY_REDEEMED
                        },
                        {
                            label: 'Cancel requested',
                            value: EGiftCardStatus.CANCEL_REQUESTED
                        }
                    ]}
                    isUpdate={
                        data?.status === EGiftCardStatus.ACTIVE ||
                        data?.status === EGiftCardStatus.PARTIALLY_REDEEMED ||
                        data?.status === EGiftCardStatus.CANCEL_REQUESTED ||
                        data?.status === EGiftCardStatus.PENDING
                    }
                    onUpdate={(val) => {
                        handleUpdateGiftCardStatus({ id: data?.id || '', status: val });
                    }}>
                    {getGiftCardStatusString(value)}
                </Status>
            )
        },
        {
            label: 'Created',
            key: 'created_at',
            isSort: true,
            render: (value: Date) => moment(value).format('MM.DD.YYYY HH:mm')
        },
        {
            label: 'Actions',
            key: 'id',
            render: (value: string, data: TAdminGiftCard) => (
                <Dropdown
                    items={[
                        {
                            label: 'Details',
                            onClick: () => {
                                setIsGiftCardDrawerOpen(true);
                                getSingleGiftCard(value);
                            }
                        }
                    ]}
                />
            )
        }
    ];

    useEffect(() => {
        setRequestSearch(search);
    }, [search]);

    useEffect(() => {
        setDebouncedRangesValue(rangesValue);
    }, [rangesValue]);

    useEffect(() => {
        if (!isFirstRender) {
            onChangeFilter(rangesValue);
        }
    }, [debouncedRangesValue]);

    useEffect(() => {
        if (requestSearch.length >= 3) {
            onChangeFilter({ search });
        } else {
            onChangeFilter({ search: undefined });
        }
    }, [requestSearch]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <Typography variant="h4">Gift Cards</Typography>
                    <Typography>{giftCardsData?.total} records found</Typography>
                </div>
                <div className={styles.actions}>
                    <Button
                        size="medium"
                        variant="primary"
                        leftIcon="filter_bars"
                        onClick={() => setIsFilterDrawerOpen(true)}>
                        Filter
                    </Button>
                </div>
            </div>
            <Table
                columns={columns}
                rows={giftCards}
                sort={{
                    key: filter.order_field || 'created_at',
                    value: filter.order as 'ASC' | 'DESC'
                }}
                onSortChange={(key, value) => {
                    onChangeFilter({
                        order: value,
                        //@ts-ignore
                        order_field: key
                    });
                }}
                filter={filter as TFilter}
                onLoadData={() => refetchGiftCards({ ...filter, page: filter.page + 1 })}
                totalRows={giftCardsData?.total || 0}
                onFilterChange={(val) => onChangeFilter(val)}
                emptyDescription="Gift card list is empty"
            />
            <Drawer
                className={styles.drawer}
                isOpen={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}>
                <Typography className={styles.title} variant="h5">
                    Filter
                </Typography>
                <Input
                    full
                    leftIcon="search"
                    value={search}
                    onChange={(val) => setSearch(val)}
                    placeholder="Search by gift card ID"
                />
                <MultiSelect
                    full
                    items={giftCardStatuses}
                    //@ts-ignore
                    values={filter.status}
                    onChange={(val) => {
                        //@ts-ignore
                        onChangeFilter({ status: val as EGiftCardStatus });
                    }}
                    label="Status"
                />
                <RangeDatePicker
                    full
                    label="Created date range"
                    values={[
                        filter.date_from?.toString() || null,
                        filter.date_to?.toString() || null
                    ]}
                    onChange={(val) =>
                        onChangeFilter({
                            date_from: val?.[0] ? new Date(val?.[0]) : undefined,
                            date_to: val?.[1] ? new Date(val?.[1]) : undefined
                        })
                    }
                    isDisableFeature
                />
                <div
                    className={styles.row}
                    onClick={() =>
                        onChangeFilter({ notification_send: !filter.notification_send })
                    }>
                    <Checkbox checked={filter.notification_send} />{' '}
                    <Typography variant="body3">Is notification sent</Typography>
                </div>
                <RangeDatePicker
                    full
                    label="Notification sent range"
                    values={[
                        filter.notification_send_date_from?.toString() || null,
                        filter.notification_send_date_to?.toString() || null
                    ]}
                    onChange={(val) =>
                        onChangeFilter({
                            notification_send_date_from: val?.[0] ? new Date(val?.[0]) : undefined,
                            notification_send_date_to: val?.[1] ? new Date(val?.[1]) : undefined
                        })
                    }
                    isDisableFeature
                />
                <RangeDatePicker
                    full
                    label="Activation date range"
                    values={[
                        filter.activation_date_from?.toString() || null,
                        filter.activation_date_to?.toString() || null
                    ]}
                    onChange={(val) =>
                        onChangeFilter({
                            activation_date_from: val?.[0] ? new Date(val?.[0]) : undefined,
                            activation_date_to: val?.[1] ? new Date(val?.[1]) : undefined
                        })
                    }
                    isDisableFeature
                />
                <Typography variant="body2" className={styles.amount}>
                    Gift card amount range
                </Typography>
                <Range
                    min={0}
                    max={1000}
                    values={[rangesValue.amount_from || 0, rangesValue.amount_to || 0]}
                    onChange={(val) =>
                        setRangesValue((state) => ({
                            ...state,
                            amount_from: val?.[0] ? Number(val[0]) : undefined,
                            amount_to: val?.[1] ? Number(val[1]) : undefined
                        }))
                    }
                />
                <Typography variant="body2" className={styles.available_amount}>
                    Available gift card amount range
                </Typography>
                <Range
                    min={0}
                    max={1000}
                    values={[
                        rangesValue.available_amount_from || 0,
                        rangesValue.available_amount_to || 0
                    ]}
                    onChange={(val) =>
                        setRangesValue((state) => ({
                            ...state,
                            available_amount_from: val?.[0] ? Number(val[0]) : undefined,
                            available_amount_to: val?.[1] ? Number(val[1]) : undefined
                        }))
                    }
                />
            </Drawer>
            <Drawer
                className={styles.transaction_drawer}
                isOpen={isGiftCardDrawerOpen && !!giftCard}
                onClose={() => {
                    setIsGiftCardDrawerOpen(false);
                    handleRemoveGiftCardData();
                }}>
                <Typography className={styles.title} variant="h5">
                    Gift card details
                </Typography>
                <Input full value={giftCard?.id} label="Gift Card ID" readOnly />
                <div className={styles.row}>
                    <Input
                        full
                        value={giftCard?.gift_card_serial_number?.toString()}
                        label="Serial number"
                        readOnly
                    />
                    <Input
                        full
                        value={getGiftCardStatusString(giftCard?.status)}
                        label="Status"
                        readOnly
                    />
                </div>
                <Textarea full value={giftCard?.description} label="Description" readOnly />
                <div className={styles.row}>
                    <Input full value={giftCard?.amount?.toString()} label="Amount" readOnly />
                    <Input
                        full
                        value={giftCard?.available_amount?.toString()}
                        label="Available amount"
                        readOnly
                    />
                    <Input full value={giftCard?.points?.toString()} label="Points" readOnly />
                    <Input full value={giftCard?.currency} label="Currency" readOnly />
                </div>
                <div className={styles.row}>
                    <Input full value={giftCard?.email || 'N/A'} label="Email" readOnly />
                    <Input
                        full
                        value={
                            giftCard?.phone_code && giftCard?.phone_number
                                ? `${giftCard?.phone_code?.includes('+') ? giftCard?.phone_code : '+' + giftCard?.phone_code} ${giftCard?.phone_number}`
                                : 'N/A'
                        }
                        label="Phone"
                        readOnly
                    />
                </div>
                <div className={styles.row}>
                    <Input
                        full
                        value={giftCard?.notification_sent ? 'Yes' : 'No'}
                        label="Is notification sent"
                        readOnly
                    />
                    <Input
                        full
                        value={
                            giftCard?.notification_sent
                                ? moment(giftCard?.notification_sent_at).format('MM.DD.YYYY HH:mm')
                                : 'N/A'
                        }
                        label="Notification sent at"
                        readOnly
                    />
                </div>
                <div className={styles.row}>
                    <Input
                        full
                        value={moment(giftCard?.created_at).format('MM.DD.YYYY HH:mm')}
                        label="Created at"
                        readOnly
                    />
                    <Input
                        full
                        value={moment(giftCard?.updated_at).format('MM.DD.YYYY HH:mm')}
                        label="Updated at"
                        readOnly
                    />
                </div>
            </Drawer>
        </div>
    );
};

export default GiftCardsLayout;
